import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { 
  Car, User, Lock, LogOut, Plus, ClipboardList, Check, 
  ChevronRight, Fuel, Gauge, Phone, AlertTriangle,
  Wrench, CheckCircle, Clock, Eye, X, Save, FileText, Camera
} from 'lucide-react';
import { PhotoGalleryEditor, buildImageUrl } from './components/depotvente/DepotVenteListingForm';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Atelier() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [technicianName, setTechnicianName] = useState('');
  const [token, setToken] = useState('');
  const [loginForm, setLoginForm] = useState({ name: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [cmsContent, setCmsContent] = useState({});
  
  const [view, setView] = useState('list'); // 'list', 'new', 'edit'
  const [intakes, setIntakes] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [intakeForm, setIntakeForm] = useState({
    plate: '',
    brand: '',
    model: '',
    mileage: '',
    fuel_level: '',
    client_name: '',
    client_phone: '',
    exterior_condition: '',
    interior_condition: '',
    items_in_vehicle: '',
    initial_observations: '',
    photos: []
  });
  
  const [interventionForm, setInterventionForm] = useState({
    work_performed: '',
    recommendations: '',
    next_service_suggestion: '',
    final_observations: ''
  });

  const [editIntakeForm, setEditIntakeForm] = useState({
    plate: '',
    brand: '',
    model: '',
    mileage: '',
    fuel_level: '',
    client_name: '',
    client_phone: '',
    exterior_condition: '',
    interior_condition: '',
    items_in_vehicle: '',
    initial_observations: '',
    photos: []
  });

  // Check for existing session on load
  useEffect(() => {
    const savedToken = localStorage.getItem('atelier_token');
    const savedName = localStorage.getItem('atelier_name');
    if (savedToken && savedName) {
      verifySession(savedToken, savedName);
    }
  }, []);

  // Fetch editable Atelier CMS content (labels/titles/CTAs) once on mount
  useEffect(() => {
    axios.get(`${API}/api/content/atelier_page`)
      .then(r => setCmsContent(r.data || {}))
      .catch(() => setCmsContent({}));
  }, []);

  const verifySession = async (t, name) => {
    try {
      await axios.get(`${API}/api/atelier/verify?token=${t}`);
      setToken(t);
      setTechnicianName(name);
      setIsLoggedIn(true);
      fetchIntakes(t);
    } catch (error) {
      localStorage.removeItem('atelier_token');
      localStorage.removeItem('atelier_name');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.name.trim() || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/api/atelier/login`, loginForm);
      setToken(response.data.token);
      setTechnicianName(response.data.name);
      setIsLoggedIn(true);
      localStorage.setItem('atelier_token', response.data.token);
      localStorage.setItem('atelier_name', response.data.name);
      toast.success(`Bienvenue ${response.data.name} !`);
      fetchIntakes(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    setTechnicianName('');
    localStorage.removeItem('atelier_token');
    localStorage.removeItem('atelier_name');
    toast.success('Déconnexion réussie');
  };

  const fetchIntakes = async (t) => {
    try {
      const response = await axios.get(`${API}/api/atelier/intakes?token=${t}`);
      setIntakes(response.data);
    } catch (error) {
      console.error('Error fetching intakes:', error);
    }
  };

  const handleCreateIntake = async (e) => {
    e.preventDefault();
    if (!intakeForm.plate.trim()) {
      toast.error('La plaque d\'immatriculation est obligatoire');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/atelier/intake?token=${token}`, {
        ...intakeForm,
        technician_name: technicianName,
        mileage: parseInt(intakeForm.mileage) || 0
      });
      toast.success('Fiche de prise en charge créée !');
      setIntakeForm({
        plate: '', brand: '', model: '', mileage: '', fuel_level: '',
        client_name: '', client_phone: '', exterior_condition: '',
        interior_condition: '', items_in_vehicle: '', initial_observations: '',
        photos: []
      });
      setView('list');
      fetchIntakes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIntake = async () => {
    if (!selectedIntake) return;
    setLoading(true);
    try {
      // Combine both forms data
      const updateData = {
        ...interventionForm,
        ...editIntakeForm,
        mileage: parseInt(editIntakeForm.mileage) || 0
      };
      await axios.put(`${API}/api/atelier/intake/${selectedIntake.id}?token=${token}`, updateData);
      toast.success('Fiche mise à jour !');
      fetchIntakes(token);
      setView('list');
      setSelectedIntake(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteIntake = async (intake) => {
    try {
      await axios.put(`${API}/api/atelier/intake/${intake.id}/status?token=${token}&status=completed`);
      toast.success('Intervention terminée !');
      fetchIntakes(token);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleStartWork = async (intake) => {
    try {
      await axios.put(`${API}/api/atelier/intake/${intake.id}/status?token=${token}&status=in_progress`);
      toast.success('Intervention démarrée !');
      fetchIntakes(token);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openIntakeForEdit = (intake) => {
    setSelectedIntake(intake);
    setInterventionForm({
      work_performed: intake.work_performed || '',
      recommendations: intake.recommendations || '',
      next_service_suggestion: intake.next_service_suggestion || '',
      final_observations: intake.final_observations || ''
    });
    setEditIntakeForm({
      plate: intake.plate || '',
      brand: intake.brand || '',
      model: intake.model || '',
      mileage: intake.mileage?.toString() || '',
      fuel_level: intake.fuel_level || '',
      client_name: intake.client_name || '',
      client_phone: intake.client_phone || '',
      exterior_condition: intake.exterior_condition || '',
      interior_condition: intake.interior_condition || '',
      items_in_vehicle: intake.items_in_vehicle || '',
      initial_observations: intake.initial_observations || '',
      photos: Array.isArray(intake.photos) ? intake.photos : []
    });
    setView('edit');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reception':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">Réception</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs font-medium">En cours</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">Terminé</span>;
      default:
        return null;
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FF3B30] rounded-full flex items-center justify-center mx-auto mb-4">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">{cmsContent.page_title || "Espace Atelier"}</h1>
            <p className="text-gray-400 mt-2">Connectez-vous pour accéder aux fiches véhicules</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm mb-2">Votre prénom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={loginForm.name}
                  onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white"
                  placeholder="Prénom"
                  data-testid="atelier-login-name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-white/80 text-sm mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white"
                  placeholder="••••••••"
                  data-testid="atelier-login-password"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-[#FF3B30] hover:bg-[#E6352B] text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50"
              data-testid="atelier-login-submit"
            >
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Atelier Interface
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF3B30] rounded-full flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold">{cmsContent.welcome_top || "Espace Atelier"}</h1>
              <p className="text-gray-400 text-sm">Connecté: {technicianName}</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* List View */}
        {view === 'list' && (
          <>
            {/* Actions */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{cmsContent.section_intakes || "Mes prises en charge"}</h2>
              <button
                onClick={() => setView('new')}
                className="flex items-center gap-2 bg-[#FF3B30] hover:bg-[#E6352B] text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                {cmsContent.cta_new || "Nouvelle prise en charge"}
              </button>
            </div>

            {/* Intakes List */}
            {intakes.length === 0 ? (
              <div className="bg-[#121212] border border-white/10 rounded-xl p-12 text-center">
                <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-white text-lg font-medium mb-2">{cmsContent.empty_intakes || "Aucune fiche"}</h3>
                <p className="text-gray-400">Créez votre première fiche de prise en charge</p>
              </div>
            ) : (
              <div className="space-y-4">
                {intakes.map((intake) => (
                  <div
                    key={intake.id}
                    className="bg-[#121212] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#1C1C1E] rounded-lg flex items-center justify-center">
                          <Car className="w-6 h-6 text-[#FF3B30]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-white font-bold text-lg">{intake.plate}</h3>
                            {getStatusBadge(intake.status)}
                          </div>
                          <p className="text-gray-400 text-sm">
                            {intake.brand} {intake.model} {intake.mileage > 0 && `• ${intake.mileage.toLocaleString()} km`}
                          </p>
                          {intake.client_name && (
                            <p className="text-gray-500 text-sm mt-1">Client: {intake.client_name}</p>
                          )}
                          <p className="text-gray-600 text-xs mt-2">
                            {new Date(intake.created_at).toLocaleDateString('fr-FR', { 
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {intake.status === 'reception' && (
                          <button
                            onClick={() => handleStartWork(intake)}
                            className="flex items-center gap-1 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 px-3 py-2 rounded-lg text-sm transition-colors"
                          >
                            <Clock className="w-4 h-4" />
                            Démarrer
                          </button>
                        )}
                        <button
                          onClick={() => openIntakeForEdit(intake)}
                          className="flex items-center gap-1 bg-white/10 text-white hover:bg-white/20 px-3 py-2 rounded-lg text-sm transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          {intake.status === 'completed' ? 'Voir' : 'Modifier'}
                        </button>
                        {intake.status === 'in_progress' && (
                          <button
                            onClick={() => handleCompleteIntake(intake)}
                            className="flex items-center gap-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 px-3 py-2 rounded-lg text-sm transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Terminer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* New Intake Form */}
        {view === 'new' && (
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{cmsContent.section_new || "Nouvelle prise en charge"}</h2>
              <button onClick={() => setView('list')} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateIntake} className="space-y-6">
              {/* Véhicule */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#FF3B30]" />
                  Informations véhicule
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Plaque *</label>
                    <input
                      type="text"
                      value={intakeForm.plate}
                      onChange={(e) => setIntakeForm({ ...intakeForm, plate: e.target.value.toUpperCase() })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white uppercase"
                      placeholder="AA-123-BB"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Marque</label>
                    <input
                      type="text"
                      value={intakeForm.brand}
                      onChange={(e) => setIntakeForm({ ...intakeForm, brand: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                      placeholder="Peugeot, Renault..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Modèle</label>
                    <input
                      type="text"
                      value={intakeForm.model}
                      onChange={(e) => setIntakeForm({ ...intakeForm, model: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                      placeholder="308, Clio..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Kilométrage</label>
                    <div className="relative">
                      <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        type="number"
                        value={intakeForm.mileage}
                        onChange={(e) => setIntakeForm({ ...intakeForm, mileage: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Niveau carburant</label>
                    <div className="relative">
                      <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <select
                        value={intakeForm.fuel_level}
                        onChange={(e) => setIntakeForm({ ...intakeForm, fuel_level: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white appearance-none"
                      >
                        <option value="">Sélectionner</option>
                        <option value="vide">Vide</option>
                        <option value="1/4">1/4</option>
                        <option value="1/2">1/2</option>
                        <option value="3/4">3/4</option>
                        <option value="plein">Plein</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Client */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#FF3B30]" />
                  Informations client (optionnel)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Nom du client</label>
                    <input
                      type="text"
                      value={intakeForm.client_name}
                      onChange={(e) => setIntakeForm({ ...intakeForm, client_name: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Téléphone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        type="tel"
                        value={intakeForm.client_phone}
                        onChange={(e) => setIntakeForm({ ...intakeForm, client_phone: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* État du véhicule */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#FF3B30]" />
                  État à la réception
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-1">État extérieur</label>
                    <textarea
                      value={intakeForm.exterior_condition}
                      onChange={(e) => setIntakeForm({ ...intakeForm, exterior_condition: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                      placeholder="Rayures, bosses, état de la carrosserie..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">État intérieur</label>
                    <textarea
                      value={intakeForm.interior_condition}
                      onChange={(e) => setIntakeForm({ ...intakeForm, interior_condition: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                      placeholder="Propreté, usure des sièges..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Objets laissés dans le véhicule</label>
                    <textarea
                      value={intakeForm.items_in_vehicle}
                      onChange={(e) => setIntakeForm({ ...intakeForm, items_in_vehicle: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                      placeholder="GPS, siège enfant, documents..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Observations initiales</label>
                    <textarea
                      value={intakeForm.initial_observations}
                      onChange={(e) => setIntakeForm({ ...intakeForm, initial_observations: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-24 resize-none"
                      placeholder="Problèmes signalés par le client, bruits, comportement du véhicule..."
                    />
                  </div>
                </div>
              </div>

              {/* Photos section (create) */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#FF3B30]" />
                  Photos de suivi
                </h3>
                <PhotoGalleryEditor
                  label="Photos à la prise en charge (rayures, tableau de bord, niveaux, pièces à contrôler…)"
                  images={intakeForm.photos}
                  onChange={(list) => setIntakeForm({ ...intakeForm, photos: list })}
                  uploadEndpoint={`/api/atelier/upload-image?token=${token}`}
                  testIdPrefix="atelier-new-form"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#FF3B30] hover:bg-[#E6352B] text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {loading ? 'Création...' : 'Créer la fiche'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Edit/View Intake */}
        {view === 'edit' && selectedIntake && (
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">{cmsContent.section_edit || "Modifier la fiche"}</h2>
                {getStatusBadge(selectedIntake.status)}
              </div>
              <button onClick={() => { setView('list'); setSelectedIntake(null); }} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Véhicule - Editable */}
            <div className="mb-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-[#FF3B30]" />
                Informations véhicule
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Plaque *</label>
                  <input
                    type="text"
                    value={editIntakeForm.plate}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, plate: e.target.value.toUpperCase() })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white uppercase"
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Marque</label>
                  <input
                    type="text"
                    value={editIntakeForm.brand}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, brand: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Modèle</label>
                  <input
                    type="text"
                    value={editIntakeForm.model}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, model: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Kilométrage</label>
                  <div className="relative">
                    <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="number"
                      value={editIntakeForm.mileage}
                      onChange={(e) => setEditIntakeForm({ ...editIntakeForm, mileage: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                      disabled={selectedIntake.status === 'completed'}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Niveau carburant</label>
                  <div className="relative">
                    <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <select
                      value={editIntakeForm.fuel_level}
                      onChange={(e) => setEditIntakeForm({ ...editIntakeForm, fuel_level: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white appearance-none"
                      disabled={selectedIntake.status === 'completed'}
                    >
                      <option value="">Sélectionner</option>
                      <option value="vide">Vide</option>
                      <option value="1/4">1/4</option>
                      <option value="1/2">1/2</option>
                      <option value="3/4">3/4</option>
                      <option value="plein">Plein</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Client - Editable */}
            <div className="mb-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF3B30]" />
                Informations client
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Nom du client</label>
                  <input
                    type="text"
                    value={editIntakeForm.client_name}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, client_name: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Téléphone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="tel"
                      value={editIntakeForm.client_phone}
                      onChange={(e) => setEditIntakeForm({ ...editIntakeForm, client_phone: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                      disabled={selectedIntake.status === 'completed'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* État à la réception - Editable */}
            <div className="mb-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#FF3B30]" />
                État à la réception
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">État extérieur</label>
                  <textarea
                    value={editIntakeForm.exterior_condition}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, exterior_condition: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                    placeholder="Rayures, bosses, état de la carrosserie..."
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">État intérieur</label>
                  <textarea
                    value={editIntakeForm.interior_condition}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, interior_condition: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                    placeholder="Propreté, usure des sièges..."
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Objets laissés dans le véhicule</label>
                  <textarea
                    value={editIntakeForm.items_in_vehicle}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, items_in_vehicle: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                    placeholder="GPS, siège enfant, documents..."
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Observations initiales</label>
                  <textarea
                    value={editIntakeForm.initial_observations}
                    onChange={(e) => setEditIntakeForm({ ...editIntakeForm, initial_observations: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-24 resize-none"
                    placeholder="Problèmes signalés par le client, bruits, comportement du véhicule..."
                    disabled={selectedIntake.status === 'completed'}
                  />
                </div>
              </div>
            </div>

            {/* Photos section (edit) */}
            <div className="mb-6">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#FF3B30]" />
                Photos de suivi ({(editIntakeForm.photos || []).length})
              </h3>
              <PhotoGalleryEditor
                label="Ajoute des photos pour tracer l'état du véhicule et les pièces réparées"
                images={editIntakeForm.photos}
                onChange={(list) => setEditIntakeForm({ ...editIntakeForm, photos: list })}
                uploadEndpoint={`/api/atelier/upload-image?token=${token}`}
                testIdPrefix="atelier-edit-form"
              />
            </div>

            {/* Intervention Form */}
            <div className="space-y-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#FF3B30]" />
                Intervention
              </h3>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Travaux effectués</label>
                <textarea
                  value={interventionForm.work_performed}
                  onChange={(e) => setInterventionForm({ ...interventionForm, work_performed: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-24 resize-none"
                  placeholder="Détail des travaux réalisés..."
                  disabled={selectedIntake.status === 'completed'}
                />
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Recommandations
                </label>
                <textarea
                  value={interventionForm.recommendations}
                  onChange={(e) => setInterventionForm({ ...interventionForm, recommendations: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-24 resize-none"
                  placeholder="Pneus à changer, freins usés, vidange à prévoir..."
                  disabled={selectedIntake.status === 'completed'}
                />
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Prochaine révision conseillée</label>
                <input
                  type="text"
                  value={interventionForm.next_service_suggestion}
                  onChange={(e) => setInterventionForm({ ...interventionForm, next_service_suggestion: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white"
                  placeholder="Ex: Vidange dans 10 000 km, contrôle technique dans 6 mois..."
                  disabled={selectedIntake.status === 'completed'}
                />
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Observations finales</label>
                <textarea
                  value={interventionForm.final_observations}
                  onChange={(e) => setInterventionForm({ ...interventionForm, final_observations: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-2.5 text-white h-20 resize-none"
                  placeholder="Autres remarques..."
                  disabled={selectedIntake.status === 'completed'}
                />
              </div>

              {selectedIntake.status !== 'completed' && (
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => { setView('list'); setSelectedIntake(null); }}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-medium transition-colors"
                  >
                    Retour
                  </button>
                  <button
                    onClick={handleUpdateIntake}
                    disabled={loading}
                    className="flex-1 bg-[#FF3B30] hover:bg-[#E6352B] text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
